import { provideHttpClient } from '@angular/common/http';
import {
    ApplicationConfig,
    inject,
    isDevMode,
    provideAppInitializer,
} from '@angular/core';
import { OverlayContainer, FullscreenOverlayContainer } from '@angular/cdk/overlay';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideFuse } from '@fuse';
import { TranslocoService, provideTransloco } from '@jsverse/transloco';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { firstValueFrom } from 'rxjs';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideHttpClient(),
        provideRouter(
            appRoutes,
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
        ),

        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'D',
                },
                display: {
                    dateInput: 'DDD',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    { id: 'zh', label: 'Chinese' },
                    { id: 'en', label: 'English' },
                    { id: 'fr', label: 'French' },
                    { id: 'de', label: 'German' },
                    { id: 'ja', label: 'Japanese' },
                    { id: 'ko', label: 'Korean' },
                    { id: 'pl', label: 'Polish' },
                    { id: 'es', label: 'Spanish' },
                ],
                defaultLang: 'en',
                fallbackLang: 'en',
                reRenderOnLangChange: true,
                prodMode: !isDevMode(),
            },
            loader: TranslocoHttpLoader,
        }),
        provideAppInitializer(() => {
            const translocoService = inject(TranslocoService);
            const availableLangs = translocoService.getAvailableLangs() as { id: string; label: string }[];
            const savedLang = localStorage.getItem('pf-lang');
            const browserLang = navigator.language?.split('-')[0]?.toLowerCase();
            const matchedSaved = savedLang && availableLangs.find(l => l.id === savedLang);
            const matchedBrowser = availableLangs.find(l => l.id === browserLang);
            const activeLang = matchedSaved ? matchedSaved.id : (matchedBrowser ? matchedBrowser.id : translocoService.getDefaultLang());
            translocoService.setActiveLang(activeLang);

            return firstValueFrom(translocoService.load(activeLang));
        }),

        // CDK: use FullscreenOverlayContainer so mat-select/mat-menu work inside fullscreen elements
        { provide: OverlayContainer, useClass: FullscreenOverlayContainer },

        // Fuse
        provideAuth(),
        provideIcons(),
        provideFuse({
            fuse: {
                layout: 'classy',
                scheme: 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-default',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),
    ],
};
